import { Camera, Position } from './types';

export class CameraController {
  private camera: Camera;
  private isDragging: boolean = false;
  private lastMousePos: Position = { x: 0, y: 0 };
  
  // Smooth tile-based zoom (tiles visible on screen)
  private tilesVisible: number = 20; // Number of tiles to show horizontally (1-40)
  private minTiles: number = 1;
  private maxTiles: number = 40;
  private zoomDirection: number = 0; // -1 for zoom in, 1 for zoom out, 0 for none
  private mapWidth: number = 0;
  private mapHeight: number = 0;
  
  // UI panel dimensions
  private readonly TOP_BAR_HEIGHT = 80;
  private readonly BOTTOM_MARGIN = 0;  // Bottom of screen is open (no UI panel)
  private readonly LEFT_PANEL_WIDTH = 280;
  private readonly RIGHT_PANEL_WIDTH = 280;
  
  // Mobile detection
  private isMobile: boolean = false;
  private isLandscape: boolean = false;
  
  // Smooth movement properties
  private targetCamera: Camera | null = null;
  private startCamera: Camera | null = null;  // Starting position for proper lerp
  private animationStartTime: number = 0;
  private animationDuration: number = 300; // 300ms for smooth camera movement
  private velocity: { x: number, y: number } = { x: 0, y: 0 };
  private movementSpeed: number = 400; // Pixels per second

  // Selected tile for zoom centering
  private selectedTile: Position | null = null;

  constructor(initialCamera: Camera) {
    this.camera = { ...initialCamera };
    this.detectMobile();
    this.updateZoomFromTiles();
    
    // Listen for orientation changes
    window.addEventListener('orientationchange', () => {
      this.detectMobile();
      this.updateZoomFromTiles();
    });
    
    window.addEventListener('resize', () => {
      this.detectMobile();
      this.updateMaxTiles();
      this.updateZoomFromTiles();
    });
  }
  
  private detectMobile(): void {
    this.isMobile = window.innerWidth <= 812 || window.innerHeight <= 812;
    this.isLandscape = window.innerWidth > window.innerHeight;
  }
  
  setMapSize(width: number, height: number): void {
    this.mapWidth = width;
    this.mapHeight = height;
    this.updateMaxTiles();
    this.clampCamera();
  }

  /**
   * Recalculate maxTiles based on map and viewport dimensions.
   * Allows zooming out exactly until the entire map fills the viewport (no gaps).
   */
  private updateMaxTiles(): void {
    if (this.mapWidth === 0 || this.mapHeight === 0) return;

    const viewport = this.getViewportBounds();

    // Calculate tilesVisible needed to exactly fit the map in each dimension
    // tilesVisible controls horizontal tile count, zoom = viewport.width / (tilesVisible * 64)
    // For width to fit: tilesVisible = mapWidth
    // For height to fit: mapHeight * 64 * zoom <= viewport.height
    //   => mapHeight * viewport.width / tilesVisible <= viewport.height
    //   => tilesVisible >= mapHeight * viewport.width / viewport.height
    const tilesForWidth = this.mapWidth;
    const tilesForHeight = this.mapHeight * (viewport.width / viewport.height);

    // maxTiles = the larger of the two (to fit BOTH dimensions)
    // No minimum - we only zoom out until the map exactly fills the viewport
    this.maxTiles = Math.max(tilesForWidth, tilesForHeight);
  }
  
  private getViewportBounds(): { width: number, height: number, offsetX: number, offsetY: number } {
    // On mobile, panels are overlay/hidden, so use full screen
    if (this.isMobile) {
      const topBarHeight = this.isLandscape ? 35 : 50;
      const bottomMargin = this.isLandscape ? 50 : 60;
      
      return {
        width: window.innerWidth,
        height: window.innerHeight - topBarHeight - bottomMargin,
        offsetX: 0,
        offsetY: topBarHeight
      };
    }
    
    // Desktop: Calculate the actual game viewport excluding UI panels
    const width = window.innerWidth - this.LEFT_PANEL_WIDTH - this.RIGHT_PANEL_WIDTH;
    const height = window.innerHeight - this.TOP_BAR_HEIGHT - this.BOTTOM_MARGIN;
    const offsetX = this.LEFT_PANEL_WIDTH;
    const offsetY = this.TOP_BAR_HEIGHT;
    
    return { width, height, offsetX, offsetY };
  }
  
  private updateZoomFromTiles(): void {
    const viewport = this.getViewportBounds();
    
    // Adjust default tiles visible based on device
    if (this.isMobile && this.tilesVisible === 20) {
      // Set better default zoom for mobile
      this.tilesVisible = this.isLandscape ? 12 : 8;
    }
    
    // Calculate zoom to fit the desired number of tiles horizontally in the viewport
    const tileSize = 64; // Base tile size
    this.camera.zoom = viewport.width / (this.tilesVisible * tileSize);
  }
  
  private clampCamera(): void {
    if (this.mapWidth === 0 || this.mapHeight === 0) return;
    
    const viewport = this.getViewportBounds();
    const tileSize = 64 * this.camera.zoom;
    
    // Calculate map dimensions in pixels
    const mapPixelWidth = this.mapWidth * tileSize;
    const mapPixelHeight = this.mapHeight * tileSize;
    
    // Calculate camera bounds
    // Camera.x and camera.y represent the top-left corner of the viewport
    const minX = viewport.offsetX - mapPixelWidth + viewport.width;
    const maxX = viewport.offsetX;
    const minY = viewport.offsetY - mapPixelHeight + viewport.height;
    const maxY = viewport.offsetY;
    
    // If map is smaller than viewport, center it
    if (mapPixelWidth < viewport.width) {
      this.camera.x = viewport.offsetX + (viewport.width - mapPixelWidth) / 2;
    } else {
      this.camera.x = Math.max(minX, Math.min(maxX, this.camera.x));
    }
    
    if (mapPixelHeight < viewport.height) {
      this.camera.y = viewport.offsetY + (viewport.height - mapPixelHeight) / 2;
    } else {
      this.camera.y = Math.max(minY, Math.min(maxY, this.camera.y));
    }
  }

  startDrag(x: number, y: number): void {
    // Block dragging during smooth animation
    if (this.isAnimating()) return;

    this.isDragging = true;
    this.lastMousePos = { x, y };
  }

  updateDrag(x: number, y: number): void {
    // Block dragging during smooth animation
    if (!this.isDragging || this.isAnimating()) return;

    const dx = x - this.lastMousePos.x;
    const dy = y - this.lastMousePos.y;

    this.camera.x += dx;
    this.camera.y += dy;

    // Apply boundaries
    this.clampCamera();

    this.lastMousePos = { x, y };
  }

  endDrag(): void {
    this.isDragging = false;
  }

  /**
   * Cancel any ongoing smooth camera animation.
   * The camera stays at its current interpolated position.
   */
  cancelSmoothAnimation(): void {
    this.targetCamera = null;
    this.startCamera = null;
    this.animationStartTime = 0;
  }

  /**
   * Check if a smooth animation is currently in progress
   */
  isAnimating(): boolean {
    return this.targetCamera !== null && this.startCamera !== null && this.animationStartTime > 0;
  }

  /**
   * Set the currently selected tile (used for zoom centering)
   */
  setSelectedTile(tile: Position | null): void {
    this.selectedTile = tile;
  }

  getCamera(): Camera {
    // Handle smooth animation for turn transitions
    if (this.targetCamera && this.startCamera && this.animationStartTime > 0) {
      const elapsed = Date.now() - this.animationStartTime;
      const progress = Math.min(elapsed / this.animationDuration, 1);

      // Smooth easing (ease-out cubic)
      const ease = 1 - Math.pow(1 - progress, 3);

      // Proper lerp from START position to TARGET position
      // This ensures the animation is independent of any external camera modifications
      this.camera.x = this.startCamera.x + (this.targetCamera.x - this.startCamera.x) * ease;
      this.camera.y = this.startCamera.y + (this.targetCamera.y - this.startCamera.y) * ease;

      if (progress >= 1) {
        this.camera = { ...this.targetCamera };
        this.targetCamera = null;
        this.startCamera = null;
        this.animationStartTime = 0;
      }
    }

    return { ...this.camera };
  }

  setPosition(x: number, y: number): void {
    this.camera.x = x;
    this.camera.y = y;
  }
  
  zoomIn(): void {
    // Single step zoom in - zoom toward selected tile
    const newTiles = Math.max(this.minTiles, this.tilesVisible - 0.5);
    if (newTiles !== this.tilesVisible) {
      this.adjustZoomToSelectedTile(newTiles);
    }
  }

  zoomOut(): void {
    // Single step zoom out - zoom toward selected tile, respect maxTiles to prevent gaps
    const newTiles = Math.min(this.maxTiles, this.tilesVisible + 0.5);
    if (newTiles !== this.tilesVisible) {
      this.adjustZoomToSelectedTile(newTiles);
    }
  }

  zoomByDelta(delta: number, mouseX?: number, mouseY?: number): void {
    // Calculate new tiles value and clamp it immediately
    const newTiles = delta < 0 ?
      Math.max(this.minTiles, this.tilesVisible - 0.5) :
      Math.min(this.maxTiles, this.tilesVisible + 0.5);

    // Only proceed if the zoom would actually change
    if (newTiles !== this.tilesVisible) {
      // Mouse wheel zoom - zoom toward mouse position
      if (mouseX !== undefined && mouseY !== undefined) {
        this.adjustZoomAtPoint(newTiles, mouseX, mouseY);
      } else {
        this.adjustZoomToSelectedTile(newTiles);
      }
    }
  }
  
  /**
   * Calculate the minimum zoom level that ensures the map fills the viewport (no gaps)
   */
  private getMinZoomToFillViewport(): number {
    if (this.mapWidth === 0 || this.mapHeight === 0) return 0.1;

    const viewport = this.getViewportBounds();
    const tileSize = 64;

    // Minimum zoom to fill width: mapWidth * tileSize * zoom >= viewport.width
    const minZoomForWidth = viewport.width / (this.mapWidth * tileSize);

    // Minimum zoom to fill height: mapHeight * tileSize * zoom >= viewport.height
    const minZoomForHeight = viewport.height / (this.mapHeight * tileSize);

    // Use the larger of the two to ensure BOTH dimensions fill
    return Math.max(minZoomForWidth, minZoomForHeight);
  }

  /**
   * Zoom toward the selected tile, or viewport center if no tile selected
   */
  private adjustZoomToSelectedTile(newTilesVisible: number): void {
    const viewport = this.getViewportBounds();
    const tileSize = 64;

    // Determine the zoom focus point (selected tile or viewport center)
    let focusWorldX: number;
    let focusWorldY: number;
    let focusScreenX: number;
    let focusScreenY: number;

    if (this.selectedTile) {
      // Zoom toward the selected tile
      focusWorldX = this.selectedTile.x + 0.5;  // Center of tile
      focusWorldY = this.selectedTile.y + 0.5;
      // Calculate where this tile currently is on screen
      focusScreenX = this.camera.x + focusWorldX * tileSize * this.camera.zoom;
      focusScreenY = this.camera.y + focusWorldY * tileSize * this.camera.zoom;
    } else {
      // No selection - zoom toward viewport center
      focusScreenX = viewport.offsetX + viewport.width / 2;
      focusScreenY = viewport.offsetY + viewport.height / 2;
      focusWorldX = (focusScreenX - this.camera.x) / (tileSize * this.camera.zoom);
      focusWorldY = (focusScreenY - this.camera.y) / (tileSize * this.camera.zoom);
    }

    // Update tiles visible
    this.tilesVisible = Math.max(this.minTiles, Math.min(this.maxTiles, newTilesVisible));
    let newZoom = viewport.width / (this.tilesVisible * tileSize);

    // ENFORCE: Never zoom out beyond where map fills viewport (no gaps)
    const minZoom = this.getMinZoomToFillViewport();
    if (newZoom < minZoom) {
      newZoom = minZoom;
      this.tilesVisible = viewport.width / (newZoom * tileSize);
    }

    // Calculate new camera position to keep the focus point stationary on screen
    this.camera.x = focusScreenX - (focusWorldX * tileSize * newZoom);
    this.camera.y = focusScreenY - (focusWorldY * tileSize * newZoom);
    this.camera.zoom = newZoom;

    // Apply boundaries
    this.clampCamera();
  }

  /**
   * Zoom toward a specific screen point (for mouse wheel zoom)
   */
  private adjustZoomAtPoint(newTilesVisible: number, mouseX: number, mouseY: number): void {
    const viewport = this.getViewportBounds();
    const tileSize = 64;

    // Calculate the world coordinates at the mouse position before zoom
    const worldMouseX = (mouseX - this.camera.x) / (tileSize * this.camera.zoom);
    const worldMouseY = (mouseY - this.camera.y) / (tileSize * this.camera.zoom);

    // Update tiles visible
    this.tilesVisible = Math.max(this.minTiles, Math.min(this.maxTiles, newTilesVisible));
    let newZoom = viewport.width / (this.tilesVisible * tileSize);

    // ENFORCE: Never zoom out beyond where map fills viewport (no gaps)
    const minZoom = this.getMinZoomToFillViewport();
    if (newZoom < minZoom) {
      newZoom = minZoom;
      this.tilesVisible = viewport.width / (newZoom * tileSize);
    }

    // Calculate new camera position to keep the same world point at the mouse position
    this.camera.x = mouseX - (worldMouseX * tileSize * newZoom);
    this.camera.y = mouseY - (worldMouseY * tileSize * newZoom);
    this.camera.zoom = newZoom;

    // Apply boundaries
    this.clampCamera();
  }

  setZoom(_zoom: number, _centerX: number, _centerY: number): void {
    // This method is now deprecated in favor of zoomIn/zoomOut
    // but kept for compatibility
  }
  
  setVelocity(dx: number, dy: number): void {
    // Block velocity changes during smooth animation (except stopping)
    if (this.isAnimating() && (dx !== 0 || dy !== 0)) {
      return;
    }
    this.velocity.x = dx;
    this.velocity.y = dy;
  }

  setZoomDirection(direction: number): void {
    // -1 for zoom in (fewer tiles), 1 for zoom out (more tiles), 0 to stop
    this.zoomDirection = direction;
    // Zooming is allowed during animation - don't cancel
  }
  
  update(deltaTime: number, _canvasWidth: number, _canvasHeight: number): void {
    // Block velocity-based movement during smooth animation
    if (!this.isAnimating() && (this.velocity.x !== 0 || this.velocity.y !== 0)) {
      this.camera.x += this.velocity.x * this.movementSpeed * deltaTime;
      this.camera.y += this.velocity.y * this.movementSpeed * deltaTime;

      // Apply boundaries
      this.clampCamera();
    }

    // Apply continuous zoom when holding +/- keys (allowed during animation)
    // Zooms toward selected tile
    if (this.zoomDirection !== 0) {
      const zoomSpeed = 20; // Tiles per second (doubled for faster zoom)
      const tileDelta = this.zoomDirection * zoomSpeed * deltaTime;
      const newTiles = this.tilesVisible + tileDelta;
      const clampedTiles = Math.max(this.minTiles, Math.min(this.maxTiles, newTiles));

      if (clampedTiles !== this.tilesVisible) {
        this.adjustZoomToSelectedTile(clampedTiles);
      }
    }
  }

  centerOn(tileX: number, tileY: number, tileSize: number, canvasWidth: number, canvasHeight: number): void {
    const tileCenterX = (tileX + 0.5) * tileSize * this.camera.zoom;
    const tileCenterY = (tileY + 0.5) * tileSize * this.camera.zoom;

    this.camera.x = canvasWidth / 2 - tileCenterX;
    this.camera.y = canvasHeight / 2 - tileCenterY;

    // Clamp to map boundaries so we don't go off the edge
    this.clampCamera();
  }

  smoothCenterOn(tileX: number, tileY: number, tileSize: number, canvasWidth: number, canvasHeight: number): void {
    const tileCenterX = (tileX + 0.5) * tileSize * this.camera.zoom;
    const tileCenterY = (tileY + 0.5) * tileSize * this.camera.zoom;

    // Calculate desired target position
    let targetX = canvasWidth / 2 - tileCenterX;
    let targetY = canvasHeight / 2 - tileCenterY;

    // Clamp target to map boundaries so we don't animate off the edge
    if (this.mapWidth > 0 && this.mapHeight > 0) {
      const viewport = this.getViewportBounds();
      const scaledTileSize = 64 * this.camera.zoom;
      const mapPixelWidth = this.mapWidth * scaledTileSize;
      const mapPixelHeight = this.mapHeight * scaledTileSize;

      const minX = viewport.offsetX - mapPixelWidth + viewport.width;
      const maxX = viewport.offsetX;
      const minY = viewport.offsetY - mapPixelHeight + viewport.height;
      const maxY = viewport.offsetY;

      // Only clamp if map is larger than viewport (otherwise centering is fine)
      if (mapPixelWidth >= viewport.width) {
        targetX = Math.max(minX, Math.min(maxX, targetX));
      }
      if (mapPixelHeight >= viewport.height) {
        targetY = Math.max(minY, Math.min(maxY, targetY));
      }
    }

    // Save the starting position for proper lerp interpolation
    this.startCamera = { ...this.camera };

    this.targetCamera = {
      x: targetX,
      y: targetY,
      zoom: this.camera.zoom
    };
    this.animationStartTime = Date.now();
    this.animationDuration = 800; // Longer duration for turn transitions
  }
}